/**
 * GT-DATA-PLANE-ADMIN-INTEGRATION-01 — typed read-only API client
 * for the Database Infrastructure Health Center.
 *
 * Wraps the registered Fastify endpoints from PR #475
 * (`/v1/admin/data-plane/*`) with the existing Admin Console
 * `apiGet` helper from `lib/api.ts`. Every call is GET-only — the
 * sprint scope explicitly forbids any mutation surface.
 *
 * Source-guard contract (`source-guards.test.ts`):
 *   - this module only calls `apiGet`, never `apiSend`;
 *   - paths begin with `/v1/admin/data-plane/`;
 *   - no fixture import;
 *   - no import from `services/**`;
 *   - no `fetch` / `XMLHttpRequest` / raw `axios`.
 */

import { apiGet, ApiError } from "../lib/api.js";
import type {
  BackupHealth,
  DataPlaneHealthOverview,
  DatastoreHost,
  Datastore,
  DbReachabilityHealth,
  MigrationHealth,
  MoveReadiness,
  TelemetryRetentionPolicy,
  TenantPlacement,
  VpnHealth,
  VpnSnapshotHistoryFilter,
  VpnSnapshotHistoryResult,
} from "./types.js";

/**
 * The list endpoints all return a single-key envelope `{ items: [...] }`
 * (see `services/api/src/modules/data-plane/routes.ts`). The
 * detail endpoints return the bare object or a 404 problem; we
 * surface 404 by returning `null` to the caller.
 */
interface ListEnvelope<T> {
  readonly items: ReadonlyArray<T>;
}

const BASE = "/v1/admin/data-plane";

export async function fetchDataPlaneOverview(
  signal?: AbortSignal,
): Promise<DataPlaneHealthOverview> {
  return apiGet<DataPlaneHealthOverview>(`${BASE}/overview`, undefined, signal);
}

export async function fetchDatastoreHosts(
  signal?: AbortSignal,
): Promise<ReadonlyArray<DatastoreHost>> {
  const res = await apiGet<ListEnvelope<DatastoreHost>>(
    `${BASE}/datastore-hosts`,
    undefined,
    signal,
  );
  return res.items;
}

export async function fetchDatastores(signal?: AbortSignal): Promise<ReadonlyArray<Datastore>> {
  const res = await apiGet<ListEnvelope<Datastore>>(`${BASE}/datastores`, undefined, signal);
  return res.items;
}

export async function fetchTenantPlacements(
  signal?: AbortSignal,
): Promise<ReadonlyArray<TenantPlacement>> {
  const res = await apiGet<ListEnvelope<TenantPlacement>>(
    `${BASE}/tenant-placements`,
    undefined,
    signal,
  );
  return res.items;
}

export async function fetchReachability(
  signal?: AbortSignal,
): Promise<ReadonlyArray<DbReachabilityHealth>> {
  const res = await apiGet<ListEnvelope<DbReachabilityHealth>>(
    `${BASE}/reachability`,
    undefined,
    signal,
  );
  return res.items;
}

export async function fetchBackupHealth(
  signal?: AbortSignal,
): Promise<ReadonlyArray<BackupHealth>> {
  const res = await apiGet<ListEnvelope<BackupHealth>>(`${BASE}/backups`, undefined, signal);
  return res.items;
}

export async function fetchMigrationHealth(
  signal?: AbortSignal,
): Promise<ReadonlyArray<MigrationHealth>> {
  const res = await apiGet<ListEnvelope<MigrationHealth>>(`${BASE}/migrations`, undefined, signal);
  return res.items;
}

export async function fetchVpnHealth(signal?: AbortSignal): Promise<ReadonlyArray<VpnHealth>> {
  const res = await apiGet<ListEnvelope<VpnHealth>>(`${BASE}/vpn`, undefined, signal);
  return res.items;
}

export async function fetchTelemetryRetention(
  signal?: AbortSignal,
): Promise<ReadonlyArray<TelemetryRetentionPolicy>> {
  const res = await apiGet<ListEnvelope<TelemetryRetentionPolicy>>(
    `${BASE}/retention`,
    undefined,
    signal,
  );
  return res.items;
}

export async function fetchMoveReadiness(
  signal?: AbortSignal,
): Promise<ReadonlyArray<MoveReadiness>> {
  const res = await apiGet<ListEnvelope<MoveReadiness>>(
    `${BASE}/move-readiness`,
    undefined,
    signal,
  );
  return res.items;
}

/**
 * Detail endpoints return either the resource or a 404 problem.
 * We swallow the 404 and return `null` so the page can render an
 * empty state without an error toast.
 */
export async function fetchDatastoreDetail(
  datastoreId: string,
  signal?: AbortSignal,
): Promise<Datastore | null> {
  try {
    return await apiGet<Datastore>(
      `${BASE}/datastores/${encodeURIComponent(datastoreId)}`,
      undefined,
      signal,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function fetchTenantPlacementDetail(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantPlacement | null> {
  try {
    return await apiGet<TenantPlacement>(
      `${BASE}/tenant-placements/${encodeURIComponent(tenantId)}`,
      undefined,
      signal,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Phase 4F — paginated VPN snapshot history.
 *
 * The backend route is `GET /v1/admin/data-plane/vpn-snapshots` with
 * cursor-style `before` / `after` ISO timestamp filters plus optional
 * `hostId` / `datastoreId` / `status` filters and a clamped `limit`.
 * The envelope is `{ items, limit, hasMore }` — no `total` count is
 * exposed because the registry table is platform-scoped and we do
 * not want the Admin Console to imply a customer-facing pagination
 * contract.
 *
 * Only defined query parameters are serialized; `undefined` values
 * are dropped so the backend's strict Zod schema does not reject the
 * request with `unrecognized_keys`.
 */
export async function fetchVpnSnapshotHistory(
  params: VpnSnapshotHistoryFilter,
  signal?: AbortSignal,
): Promise<VpnSnapshotHistoryResult> {
  const query: Record<string, string | number> = {};
  if (params.hostId !== undefined) query.hostId = params.hostId;
  if (params.datastoreId !== undefined) query.datastoreId = params.datastoreId;
  if (params.status !== undefined) query.status = params.status;
  if (params.before !== undefined) query.before = params.before;
  if (params.after !== undefined) query.after = params.after;
  if (params.limit !== undefined) query.limit = params.limit;
  return apiGet<VpnSnapshotHistoryResult>(`${BASE}/vpn-snapshots`, query, signal);
}
